<template>
    <div
        ref="slider"
        class="image-slider"
        :class="{ 'auto-height': !hasMultipleImages }"
        :style="`max-height:${maxHeight}px;`"
    >
        <div
            :class="['slides-wrapper', { 'slides-wrapper--not-animated': isAnimationDisabled }]"
            :style="{ transform: `translateX(${currentSliderTranslate}px)` }"
        >
            <div
                v-for="(image, index) in imagePaths"
                :key="image"
                class="slide"
            >
                <img
                    :src="image"
                    draggable="false"
                    class="slider-image"
                    :style="`max-height:${maxHeight}px;`"
                    onerror="this.style.opacity='0'"
                    :alt="getImageAlt(index)"
                    @click="handleImageClick"
                >
            </div>
        </div>
        <div
            v-if="hasMultipleImages"
            class="slider-button previous"
            @click="previousSlide"
        >
            <span class="slider-button__icon" />
        </div>
        <div
            v-if="hasMultipleImages"
            class="slider-button next"
            @click="nextSlide"
        >
            <span class="slider-button__icon" />
        </div>
        <div
            v-if="hasMultipleImages"
            class="slider-image-counter f-14"
        >
            {{ (currentSlideIndex + 1) + '/' + imageCount }}
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { PropType } from 'vue';
    import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
    import { useAccountStore } from '~/stores/accountStore';
    import { useModalStore } from '~/stores/modalStore';
    import { useElementSize } from '@vueuse/core';
    import { useI18n } from 'vue-i18n';

    const props = defineProps({
        imagePaths: {
            type: Array as PropType<string[]>,
            required: true,
        },
        maxHeight: {
            type: Number,
            required: false,
            default: 450
        },
        arrowNavigationAllowed: {
            type: Boolean,
            required: false,
            default: false
        },
        initialImageIndex: {
            type: Number,
            required: false,
            default: undefined
        },
        altText: {
            type: String,
            required: true,
        }
    });

    const modalStore = useModalStore();
    const { buttonColor } = useAccountStore().state;
    const slider = ref<HTMLElement | null>(null);
    const currentSlideIndex = ref<number>(0);
    const currentSliderTranslate = ref<number>(0);
    const swipeStartX = ref<number>(0);
    const swipeEndX = ref<number>(0);
    const isAnimationDisabled = ref(false);

    const { t } = useI18n();

    const imageCount = computed(() => {
        return props.imagePaths.length;
    });

    const hasMultipleImages = computed(() => {
        return imageCount.value > 1;
    });

    const isFirstImage = computed(() => {
        return currentSlideIndex.value === 0;
    });

    const isLastImage = computed(() => {
        return currentSlideIndex.value === props.imagePaths.length - 1;
    });

    const previousSlide = () => {
        if (isFirstImage.value) {
            currentSlideIndex.value = props.imagePaths.length - 1;
        } else {
            currentSlideIndex.value--;
        }

        setCurrentTranslate();
    };

    const nextSlide = () => {
        if (isLastImage.value) {
            currentSlideIndex.value = 0;
        } else {
            currentSlideIndex.value++;
        }

        setCurrentTranslate();
    };

    const setCurrentTranslate = () => {
        if (!slider.value) {
            return;
        }

        currentSliderTranslate.value = currentSlideIndex.value * slider.value.clientWidth * -1;
    };

    const handleTouchStart = (e: TouchEvent) => {
        swipeStartX.value = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
        swipeEndX.value = e.changedTouches[0].clientX;

        if (swipeEndX.value + 30 < swipeStartX.value) {
            nextSlide();
        }

        if (swipeEndX.value - 30 > swipeStartX.value) {
            previousSlide();
        }
    };

    const handleImageClick = () => {
        // prevent opening modal on small screens
        if (window.innerWidth < 500) {
            return;
        }

        modalStore.setModalData(
            props.imagePaths[0],
            props.imagePaths,
            true,
        );
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (!props.arrowNavigationAllowed) {
            return;
        }

        if (event.key === 'ArrowLeft') {
            previousSlide();
        }

        if (event.key === 'ArrowRight') {
            nextSlide();
        }
    };

    const setInitialImageIndex = () => {
        if (typeof props.initialImageIndex === 'undefined') {
            return;
        }
        isAnimationDisabled.value = true;
        const imagesCount = props.imagePaths.length;
        const index = props.initialImageIndex < imagesCount ? props.initialImageIndex : imagesCount - 1;
        currentSlideIndex.value = index;
        setCurrentTranslate();
        setTimeout(() => isAnimationDisabled.value = false);
    };

    const getImageAlt = (index: number) => {
        return `${props.altText} - ${t('common.slider_counter', {
            index: index + 1,
            total: imageCount.value
        })}`;
    };

    const { width: sliderWidth } = useElementSize(slider);
    watch(sliderWidth, (newWidth) => !!newWidth && setInitialImageIndex(), { immediate: true });

    onMounted(() => {
        // touch functionality allows users to swipe images on mobile devices
        slider.value?.addEventListener('touchstart', handleTouchStart);
        slider.value?.addEventListener('touchend', handleTouchEnd);
        document.addEventListener('keydown', handleKeyDown);
    });

    onBeforeUnmount(() => {
        slider.value?.removeEventListener('touchstart', handleTouchStart);
        slider.value?.removeEventListener('touchend', handleTouchEnd);
        document.removeEventListener('keydown', handleKeyDown);
    });
</script>

<style scoped lang="scss">
    .image-slider {
        position: relative;
        min-width: 100%;
        height: auto;
        background-color: #f7f7f7;
        overflow-x: hidden;
    }

    .image-slider.auto-height {
        height: auto !important;
    }

    .slides-wrapper {
        display: flex;
        max-width: 750px;
        align-items: center;
        height: 100%;

        &:not(.slides-wrapper--not-animated) {
          transition: all 0.6s ease;
        }
    }

    .slide {
        display: flex;
        min-width: 100%;
        height: 100%;
        justify-content: center;
        align-items: center;
    }

    .slide.show {
        display: flex;
    }

    .slider-image {
        object-fit: contain;
        max-width: 100%;

        @media (max-width: 767px) {
            max-height: 370px;
        }
    }

    .slider-button {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        color: #ffffff;
        padding: 1rem;
        cursor: pointer;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        transition: all 0.3s ease;

        &__icon {
          position: absolute;
          cursor: pointer;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          box-shadow: 0px 0px 2px 1px #00000012;
          background-color: v-bind(buttonColor);

          &::before, &::after {
            content: "";
            display: block;
            width: 2px;
            height: 6px;
            background-color: #ffffff;
            margin: auto;
          }

          &::before {
            margin-top: 11px;
          }
          &:after {
            margin-top: -3px;
          }
        }
    }

    .slider-button.previous {
        left: 4px;
        .slider-button__icon {
          &::before {
            transform: rotate(45deg);
          }
          &::after {
            transform: rotate(-45deg);
          }
        }
    }

    .slider-button.next {
        right: 4px;
        .slider-button__icon {
          &::before {
            transform: rotate(-45deg);
          }
          &::after {
            transform: rotate(45deg);
          }
        }
    }

    .slider-image-counter {
        font-size: 0.8rem;
        position: absolute;
        bottom: 10px;
        right: 10px;
        background-color: #ffffff;
        padding: 0.35rem 0.4rem;
        border-radius: 0.4rem;
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.07);
    }

    @media (hover: hover) and (pointer: fine) {
      .slider-button {
        opacity: 0;
        &.previous {
          left: -7px;
        }
        &.next {
          right: -7px;
        }
        &:hover {
          filter: brightness(120%);
        }
      }
      .image-slider:hover .slider-button {
        opacity: 1;
        &.previous {
          left: 4px;
        }
        &.next {
          right: 4px;
        }
      }
    }
</style>
